import { linkOptions, useNavigate, useParams, useSearch } from '@tanstack/react-router';

import { Breadcrumbs } from '../../../cdl/Breadcrumbs/Breadcrumbs';
import { Button } from '../../../cdl/Button/Button';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { Tabs } from '../../../cdl/Tabs/Tabs';
import { Text } from '../../../cdl/Text/Text';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { translate } from '../../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../../common/hooks/useDocumentTitle';
import { useRole } from '../../../common/hooks/useRole';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../../common/Page/Page';
import { Box } from '../../../common/ui/Box';
import { SupplierMfaSettings } from '../../customer/detail/components/MfaSettings';
import { SupplierGroupTabs } from '../SupplierGroupTabs';

import { CreateSupplierCompany } from './components/CreateSupplierCompany';
import { EditSupplierGroupForm } from './components/EditSupplierGroupForm';
import { SpotPortAvailabilityTabs } from './components/SpotPortAvailabilityTabs';
import { SupplierSsoSettings } from './components/sso/SupplierSsoSettings';
import { SupplierDevelopersTab } from './components/SupplierDevelopersTab';
import { SupplierGroupDataSheet } from './components/SupplierGroupDataSheet';
import { SupplierGroupImageDisplay } from './components/SupplierGroupImageDisplay';
import { SuppliersTable } from './components/SuppliersTable';
import { Surcharges } from './components/Surcharges';
import { useSupplierGroupDetails } from './hooks/useSupplierGroupDetails';

export const SupplierGroupDetailPage = () => {
    const { groupId } = useParams({ from: '/_app/supplier-groups/$groupId/' });

    const role = useRole();
    const { push, pop } = useDrawer();
    const navigate = useNavigate({ from: '/supplier-groups/$groupId' });

    const supplierGroupDetailsQuery = useSupplierGroupDetails(groupId);

    useDocumentTitle(translate('settings.groupManagement.title'));

    const { tab } = useSearch({ from: '/_app/supplier-groups/$groupId/' });

    if (supplierGroupDetailsQuery.isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (supplierGroupDetailsQuery.isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const supplierGroup = supplierGroupDetailsQuery.data;

    const handleSupplierGroupEditClick = () => {
        const drawerId = push({
            header: { title: translate('suppliergroup.editTitle') },
            content: <EditSupplierGroupForm onSuccess={() => pop(drawerId)} initialValues={supplierGroup} />,
        });
    };

    const handleCompanyCreateClick = () => {
        const drawerId = push({
            header: { title: translate('supplier.createtitle') },
            content: (
                <CreateSupplierCompany
                    onSuccess={() => pop(drawerId)}
                    groupId={groupId}
                    isTestGroup={supplierGroup.testCompany}
                />
            ),
        });
    };

    const breadcrumbLinks = role.isSuperAdmin()
        ? [{ label: translate('page.suppliers'), linkOptions: linkOptions({ to: '/supplier-groups' }) }]
        : [{ label: translate('global.settings'), linkOptions: linkOptions({ to: '/settings' }) }];

    const isSpotSupplier = supplierGroup.spotAllowed;
    const hasSpotContexts = supplierGroup.lubesSettings.spotAllowed;
    const canViewSpotTab = (role.hasAdminRights() || role.isSuperAdmin()) && isSpotSupplier && hasSpotContexts;

    const tabTotals = {
        [SupplierGroupTabs.DETAILS]: null,
        ...(role.isSuperAdmin() ? { [SupplierGroupTabs.SURCHARGES]: null } : {}),
        ...(canViewSpotTab ? { [SupplierGroupTabs.SPOT_PORT_AVAILABILITY]: null } : {}),
        [SupplierGroupTabs.TWO_STEP_AUTH]: null,
        [SupplierGroupTabs.SSO]: null,
        ...(role.hasAdminRights() || role.isSuperAdmin() ? { [SupplierGroupTabs.DEVELOPERS]: null } : {}),
    };

    return (
        <Page>
            <Box display="flex" flexDirection="column" gap={4} marginBottom={16}>
                <Breadcrumbs links={breadcrumbLinks} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Text variant="headline" as="h1">
                        {translate('settings.groupManagement.title')}
                    </Text>
                </Box>

                <Text variant="body" color="foreground.subtle">
                    {supplierGroup.name}
                </Text>
            </Box>

            <Tabs
                selectedContext={tab}
                onSelect={(newTab: SupplierGroupTabs) =>
                    navigate({ search: (previousSearch) => ({ ...previousSearch, tab: newTab }) })
                }
                totals={tabTotals}
            />

            <Box marginTop={40}>
                {tab === SupplierGroupTabs.DETAILS ? (
                    <Box display="flex" flexDirection="column" gap={48}>
                        <Box display="grid" gridTemplateColumns="1fr 300px" alignItems="start" gap={40}>
                            <Box display="flex" flexDirection="column" gap={16}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Text variant="subtitle1" as="h2">
                                        {translate('suppliers.detailsSectionHeadline')}
                                    </Text>
                                    {role.hasAdminRights() || role.isSuperAdmin() ? (
                                        <Button emphasis="medium" onClick={handleSupplierGroupEditClick}>
                                            {translate('actions.edit')}
                                        </Button>
                                    ) : null}
                                </Box>
                                <SupplierGroupDataSheet supplierGroup={supplierGroup} />
                            </Box>
                            <Box display="flex" flexDirection="column" gap={16}>
                                <Text variant="subtitle1" as="h2">
                                    {translate('suppliergroup.logo')}
                                </Text>
                                <SupplierGroupImageDisplay logo={supplierGroup.logo} id={groupId} />
                            </Box>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={16}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Text variant="subtitle1" as="h2">
                                    {translate('suppliers.subheadline')}
                                </Text>
                                {role.hasAdminRights() ? (
                                    <Button LeadingIcon={IconPlus} onClick={handleCompanyCreateClick}>
                                        {translate('suppliers.create')}
                                    </Button>
                                ) : null}
                            </Box>
                            <SuppliersTable groupId={groupId} />
                        </Box>
                    </Box>
                ) : null}

                {tab === SupplierGroupTabs.SPOT_PORT_AVAILABILITY ? (
                    <SpotPortAvailabilityTabs groupId={groupId} supplierGroup={supplierGroup} />
                ) : null}

                {tab === SupplierGroupTabs.TWO_STEP_AUTH ? <SupplierMfaSettings groupId={groupId} /> : null}

                {tab === SupplierGroupTabs.SSO ? <SupplierSsoSettings groupId={groupId} /> : null}

                {tab === SupplierGroupTabs.DEVELOPERS ? <SupplierDevelopersTab groupId={groupId} /> : null}

                {tab === SupplierGroupTabs.SURCHARGES ? <Surcharges groupId={groupId} /> : null}
            </Box>
        </Page>
    );
};
